import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useParams } from "react-router";
import { useTranslation } from "react-i18next";

import {
  GetCardReaders,
  PutCardReader,
  DeleteCardReader,
} from "api/rpc/2024-04/facilityAdmin/payment/teeonPayments/reader";
import { StatusCode } from "api/protocols";

import {showError, showSuccess } from "redux/actions/ui";

import Page from "components/page/Page";
import Card from "components/card/Card";
import FormLayout from "components/form/FormLayout";
import Form from "components/form/Form";
import Input from "components/form/input/Input";

import { useAppDispatch, useAppSelector } from "hooks/redux";
import { removeReader } from "redux/actions/facilityAdmin/terminal";
import Popup from "components/popup/Popup";
import { isEqualWith } from "lodash";


interface IParams {
  id: string;
}

type CardReaderState = {
  id: number | null;
  label: string;
  serial_number: string;
};

export default function CardReader() {


  const dispatch = useAppDispatch();
  const { terminalStore } = useAppSelector(store => store);

  const history = useHistory();
  const { t } = useTranslation();
  const { id } = useParams<IParams>();
  const [state, setState] = useState<CardReaderState>({
    id: null,
    label: "",
    serial_number: "",
  });
  const [showWarningState, setShowWarningState] = useState<boolean>(false);
  useEffect(() => {
    void loadCardReaders();
  }, []);

  const [cardReaderBeforeChanges, setCardReaderBeforeChanges] = useState<CardReaderState | undefined>(undefined);
  const [cardReaderLoaded, setCardReaderLoaded] = useState<boolean>(false);

  async function loadCardReaders() {
    const res = await GetCardReaders({ id: Number(id) }, true);
    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("Error getting card reader")));
      history.push("/admin/settings/card_reader");
      return;
    }

    const cardReader = res.data[0];

    setState(prevState => ({
      ...prevState,
      id: cardReader.id,
      label: cardReader.label,
      serial_number: cardReader.serial_number,
    }));

    setCardReaderLoaded(true);
  }

  async function saveCardReader() {
    const res = await PutCardReader(
      {
        id: state.id,
        label: state.label,
      },
      true,
    );

    if (res.status === StatusCode.OK) {
      dispatch(showSuccess(t("Reader updated successfully")));
      history.push("/admin/settings/card_reader");
    } else {
      dispatch(showError(t("Error saving card reader")));
    }
  }

  async function deleteCardReader() {
    if (state.id === null) {
      dispatch(showError(t("Error ocurred when deleting card reader")));
      return;
    }

    const res = await DeleteCardReader(
      {
        id: state.id,
      },
      true,
    );

    if (res.status === StatusCode.OK) {
      dispatch(showSuccess(t("Reader deleted successfully")));

      console.log("terminal Store", terminalStore);

      // If the reader is currently selected, remove it
      if (terminalStore.reader?.serial_number === state.serial_number) {
        dispatch(removeReader(true));
      }

      history.push("/admin/settings/card_reader");
    } else {
      dispatch(showError(t("Error ocurred when deleting card reader")));
    }
  }

  function handleInputChange(event: any) {
    const { id, value } = event.target;
    setState(prevState => ({ ...prevState, [id]: value }));
  }

  function handleDelete() {
    setShowWarningState(true);
  }

  const secondaryAction = [
    {
      content: t("Delete"),
      action: handleDelete,
    },
  ];

  function unsavedChangesExist() {
    if (cardReaderBeforeChanges === undefined) {
      if (cardReaderLoaded) {
        setCardReaderBeforeChanges(state);
      }
      return false;
    }

    return !isEqualWith(cardReaderBeforeChanges, state);
  }

  function cancelUnsavedChanges() {
    if (cardReaderBeforeChanges) {
      setState(cardReaderBeforeChanges);
    }
  }

  return (
    <Page
      title={state.label}
      subtitle={state.serial_number}
      narrow
      breadcrumbs={[
        {
          prefix: true,
          label: t("Hardware"),
          url: "/admin/settings/card_reader",
        },
      ]}
      // primaryAction={primaryAction}
      secondaryActions={secondaryAction}
      notificationBarProps={{
        isVisible: unsavedChangesExist(),
        onAction: saveCardReader,
        onCancel: cancelUnsavedChanges,
      }}
    >
      <Card>
        <Card.Section>
          <Form>
            <FormLayout>
              <FormLayout.Group>
                <Input
                  value={state.label}
                  label={t("Label")}
                  id="label"
                  onChange={handleInputChange}
                  placeholder={t("Label")}
                />
              </FormLayout.Group>
            </FormLayout>
          </Form>
        </Card.Section>
      </Card>
      <Popup
        open={showWarningState}
        type={"warning"}
        stacked
        title={t("Delete Card Reader?")}
        onCancel={() => setShowWarningState(false)}
        cancelText={t("Cancel")}
        okText={t("Delete")}
        onOk={deleteCardReader}
        description={t("Deleting this card reader will make it unusable for this facility, continue?")}
      />
    </Page>
  );
}
